import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ShieldCheck, Truck, Lock } from "lucide-react";

/**
 * Hero-adjacent trust strip. Three restrained signals:
 *   1. Live approved-vendor count (from /api/stats/public — no inflation).
 *   2. Same-day / hyperlocal delivery promise (static; core value prop).
 *   3. Secure Checkout — mentions Razorpay-managed UPI/Cards/NetBanking.
 * Deliberately does NOT show "orders delivered" — will remain hidden until real volume exists.
 */
export default function TrustBadges() {
  const [stats, setStats] = useState({ approved_vendors: null });

  useEffect(() => {
    let cancelled = false;
    api.get("/stats/public")
      .then(r => { if (!cancelled) setStats(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // If we don't yet have a real number, hide the vendor-count tile (never show a
  // placeholder low number). Delivery + secure-checkout are static and always shown.
  const vendorCount = stats.approved_vendors;
  const showVendorCount = typeof vendorCount === "number" && vendorCount >= 10;   // "1+ shops" reads oddly

  return (
    <section
      data-testid="trust-badges"
      className="max-w-7xl mx-auto px-6 pt-2 pb-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {showVendorCount && (
          <TrustCard
            icon={ShieldCheck}
            tone="teal"
            testid="trust-verified-vendors"
            title={`${vendorCount}+ verified local vendors`}
            body="Every shop is manually reviewed before going live."
          />
        )}
        <TrustCard
          icon={Truck}
          tone="marigold"
          testid="trust-same-day"
          title="Delivered in hours, not days"
          body="Same-day hyperlocal delivery from shops near you."
        />
        <TrustCard
          icon={Lock}
          tone="stone"
          testid="trust-secure-checkout"
          title="Secure checkout"
          body={<>UPI · Cards · NetBanking <span className="text-stone-400">·</span> processed via Razorpay</>}
        />
      </div>
    </section>
  );
}

function TrustCard({ icon: Icon, tone, testid, title, body }) {
  const tones = {
    teal: "bg-teal/10 text-teal border-teal/20",
    marigold: "bg-marigold/10 text-marigold border-marigold/20",
    stone: "bg-stone-100 text-stoneWarm border-stone-200",
  };
  return (
    <div
      data-testid={testid}
      className={`flex items-start gap-3 rounded-2xl border bg-white p-4 ${tones[tone].replace(/bg-\S+/, "").trim()} shadow-sm`}
    >
      <div className={`h-10 w-10 shrink-0 rounded-xl flex items-center justify-center border ${tones[tone]}`}>
        <Icon className="h-5 w-5" strokeWidth={2.25} />
      </div>
      <div className="min-w-0">
        <div className="font-heading text-sm font-semibold text-stoneWarm leading-tight">{title}</div>
        <div className="text-xs text-stone-600 mt-0.5 leading-snug">{body}</div>
      </div>
    </div>
  );
}

/**
 * Compact single-line payment trust badge. Reused in the footer and cart summary
 * so every page carries a baseline secure-checkout signal regardless of entry point.
 */
export function PaymentTrustBadge({ testid = "payment-trust-badge", className = "" }) {
  return (
    <div
      data-testid={testid}
      className={`inline-flex items-center gap-2 text-[11px] text-stone-600 ${className}`}
    >
      <Lock className="h-3 w-3 text-emerald-600" />
      <span className="font-semibold text-stoneWarm">Secure checkout</span>
      <span className="text-stone-400">·</span>
      <span className="font-mono tracking-wide">UPI</span>
      <span className="text-stone-400">·</span>
      <span className="font-mono tracking-wide">Cards</span>
      <span className="text-stone-400">·</span>
      <span className="font-mono tracking-wide">NetBanking</span>
      <span className="text-stone-400">·</span>
      <span>via Razorpay</span>
    </div>
  );
}
